import React, { useRef, useState, useEffect, useMemo } from "react";
import JoditEditor from "jodit-react";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../hooks/useAppDispatch.tsx";
import {
  addFAQ,
  updateFAQ,
  FAQ,
  fetchFAQs,
} from "../../../redux/features/faq/faqSlice.tsx";

const editorStyles = `
  .jodit-container ul {
    
    padding-left: 40px !important;
  }
  .jodit-container ol {
    
    padding-left: 40px !important;
  }
`;

interface FAQEditorProps {
  faq: FAQ | null;
  onCancel: () => void;
  selectedSubcategory: string;
}

const FAQEditor: React.FC<FAQEditorProps> = ({
  faq,
  onCancel,
  selectedSubcategory,
}) => {
  const dispatch = useAppDispatch();
  const editorRef = useRef(null);

  const [questionContent, setQuestionContent] = useState("");
  const [answerContent, setAnswerContent] = useState("");
  const [initialQuestionContent, setInitialQuestionContent] = useState("");
  const [initialAnswerContent, setInitialAnswerContent] = useState("");

  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.innerHTML = editorStyles;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  useEffect(() => {
    if (faq) {
      setQuestionContent(faq.questionEn);
      setAnswerContent(faq.answerEn);
      setInitialQuestionContent(faq.questionEn);
      setInitialAnswerContent(faq.answerEn);
    }
  }, [faq]);

  const configQuestion = useMemo(
    () => ({

      readonly: false,
      placeholder: faq ? "" : "Add Question", // No placeholder if editing
      enableDragAndDropFileToEditor: false,
    }),
    [faq]
  );

  const configAnswer = useMemo(
    () => ({
      readonly: false,
      placeholder: faq ? "" : "Add Answer", // No placeholder if editing
      enableDragAndDropFileToEditor: false,
    }),
    [faq]
  );
  const handleQuestionChange = (newContent: string) => {
    setQuestionContent(newContent);
  };

  const handleAnswerChange = (newContent: string) => {
    setAnswerContent(newContent);
  };

  const handlePublish = async () => {
    const newFaq = {
      type: 2,
      questionEn: questionContent,
      answerEn: answerContent,
      createdAt: new Date().toISOString(),
      lang: selectedSubcategory,
    };

    try {
      const resultAction = await dispatch(addFAQ(newFaq));
      if (addFAQ.fulfilled.match(resultAction)) {
        onCancel();
        toast.success("FAQ Published successfully");
        // Fetch updated FAQs after adding
        dispatch(fetchFAQs({ type: 2, lang: selectedSubcategory }));
      }
    } catch (error) {
      console.error("Error adding FAQ:", error);
    }
  };

  const handleUpdate = async () => {
    if (!faq?.contentId) return;

    const updatedFaq = {
      ...faq,
      questionEn: questionContent,
      answerEn: answerContent,
    };

    try {
      const resultAction = await dispatch(updateFAQ(updatedFaq));
      if (updateFAQ.fulfilled.match(resultAction)) {
        onCancel();
        toast.success("FAQ updated successfully");
        // Fetch updated FAQs after updating
        dispatch(fetchFAQs({ type: 2, lang: selectedSubcategory }));
      }
    } catch (error) {
      console.error("Error updating FAQ:", error);
    }
  };

  const isQuestionChanged = questionContent !== initialQuestionContent;
  const isAnswerChanged = answerContent !== initialAnswerContent;
  const isAnyFieldEmpty = !questionContent.trim() || !answerContent.trim();
  const isButtonDisabled =
    (!isQuestionChanged && !isAnswerChanged) || isAnyFieldEmpty;

  return (
    <div className="border rounded-xl shadow">
      <div className="flex justify-between items-center bg-[#E3F7F9] p-4 h-16 rounded-t-lg">
        <h1 className="font-bold text-2xl text-[#004135]">
          {faq?.contentId ? "Edit FAQ" : "Add New FAQaa"}
        </h1>
      </div>
      <div className="p-4">
        <div
          className="mb-4 p-4"
          style={{ border: "1px solid #C4C4C4", borderRadius: "12px" }}
        >
          <label className="block mb-2 font-bold">Add Question</label>
          <JoditEditor
            ref={editorRef}
            value={questionContent}
            config={configQuestion}
            onChange={handleQuestionChange}
          />
        </div>
        <div
          className="mb-4 p-4"
          style={{ border: "1px solid #C4C4C4", borderRadius: "12px" }}
        >
          <label className="block mb-2 font-bold">Add Answer</label>
          <JoditEditor
            ref={editorRef}
            value={answerContent}
            config={configAnswer}
            onChange={handleAnswerChange}
          />
        </div>
        <div className="flex justify-between">
          <Button
            variant="outlined"
            onClick={onCancel}
            style={{
              color: "#111",
              border: "1px solid #C4C4C4",
              borderRadius: "10px",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={faq?.contentId ? handleUpdate : handlePublish}
            disabled={isButtonDisabled}
            style={{
              backgroundColor: isButtonDisabled ? "#C4C4C4" : "#3BC1CD",
              color: "#FFF",
              borderRadius: "10px",
              cursor: isButtonDisabled ? "not-allowed" : "pointer",
            }}
          >
            {faq?.contentId ? "Update" : "Publish"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FAQEditor;
